/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/modular-form/custom/GoogleMapsAddressAutocomplete.tsx
 * Release: 2.1.0 - Google Places Autocomplete per indirizzi
 * Descrizione: Componente per autocomplete indirizzi con Google Places API
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, MapPin, AlertCircle, Loader } from 'lucide-react';
import { GOOGLE_MAPS_CONFIG } from '../../../lib/config';
import { googleMapsService } from '../../../services/googleMapsService';


interface GoogleMapsAddressAutocompleteProps {
  value?: string;
  onChange?: (value: string) => void;
  onPlaceSelect?: (place: any) => void;
  onFocus?: () => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

const MAX_SEARCHES_PER_SESSION = 50;
const SEARCH_COUNT_KEY = 'google_address_search_count';

export const GoogleMapsAddressAutocomplete: React.FC<GoogleMapsAddressAutocompleteProps> = ({
  value = '',
  onChange,
  onPlaceSelect,
  onFocus,
  disabled = false,
  placeholder = 'Cerca indirizzo...',
  className = ''
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchCount, setSearchCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [googleLoaded, setGoogleLoaded] = useState(false);

  // Non servono più ref per i servizi, usa il servizio centralizzato
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Verifica se Google Maps è disabilitato
  const isGoogleMapsDisabled = GOOGLE_MAPS_CONFIG.DISABLED || !GOOGLE_MAPS_CONFIG.API_KEY;

  // Inizializzazione semplice: il servizio centralizzato gestisce tutto
  useEffect(() => {
    if (isGoogleMapsDisabled) {
      return;
    }

    setGoogleLoaded(true); // Il servizio centralizzato gestisce l'inizializzazione
    console.log('[GoogleMapsAddressAutocomplete] Usando servizio centralizzato Google Maps');
  }, [isGoogleMapsDisabled]);

  // Carica il contatore delle ricerche
  useEffect(() => {
    const savedCount = sessionStorage.getItem(SEARCH_COUNT_KEY);
    if (savedCount) {
      setSearchCount(parseInt(savedCount, 10));
    }
  }, []);

  // Salva il contatore delle ricerche
  useEffect(() => {
    sessionStorage.setItem(SEARCH_COUNT_KEY, searchCount.toString());
  }, [searchCount]);

  // Aggiorna il valore dell'input quando cambia value
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  // Debounced search usando il servizio centralizzato
  const debouncedSearch = useCallback(
    debounce(async (input: string) => {
      if (isGoogleMapsDisabled || !googleLoaded || !input.trim() || searchCount >= MAX_SEARCHES_PER_SESSION) {
        setPredictions([]);
        setShowDropdown(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        console.log('[GoogleMapsAddressAutocomplete] 🔍 Ricerca con servizio centralizzato:', input);
        const predictions = await googleMapsService.getAutocompletePredictions(input, {
          input,
          types: ['address'],
          componentRestrictions: { country: 'it' }
        });
        
        setIsLoading(false);
        setPredictions(predictions);
        setShowDropdown(predictions.length > 0);
        
        // Incrementa il contatore solo se abbiamo ottenuto risultati
        if (predictions.length > 0) {
          setSearchCount(prev => prev + 1);
        }
      } catch (error) {
        console.error('[GoogleMapsAddressAutocomplete] Errore ricerca:', error);
        setIsLoading(false);
        setError('Errore nella ricerca. Prova di nuovo.');
        setPredictions([]);
        setShowDropdown(false);
      }
    }, 500),
    [googleLoaded, searchCount, isGoogleMapsDisabled]
  );

  // Gestisce il cambio di input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange?.(newValue);
    
    if (isGoogleMapsDisabled) {
      return;
    }
    
    if (searchCount >= MAX_SEARCHES_PER_SESSION) {
      setError(`Limite di ${MAX_SEARCHES_PER_SESSION} ricerche raggiunto per questa sessione`);
      return;
    }
    
    debouncedSearch(newValue);
  };

  // Gestisce la selezione di un posto usando il servizio centralizzato
  const handlePlaceSelect = async (prediction: google.maps.places.AutocompletePrediction) => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('[GoogleMapsAddressAutocomplete] 📍 Dettagli luogo con servizio centralizzato:', prediction.place_id);
      const place = await googleMapsService.getPlaceDetails(prediction.place_id, [
        'formatted_address', 
        'address_components', 
        'geometry'
      ]);
      
      setIsLoading(false);
      
      if (place) {
        // Incrementa il contatore delle ricerche per i dettagli
        setSearchCount(prev => prev + 1);
        
        // Aggiorna il valore dell'input
        setInputValue(prediction.description);
        onChange?.(prediction.description);
        
        // Chiama la callback con i dettagli del posto
        if (onPlaceSelect) {
          onPlaceSelect({
            formatted_address: place.formatted_address,
            address_components: place.address_components,
            geometry: place.geometry
          });
        }
        
        setShowDropdown(false);
        setPredictions([]);
      } else {
        setError('Errore nel recupero dei dettagli dell\'indirizzo');
      }
    } catch (error) {
      console.error('[GoogleMapsAddressAutocomplete] Errore dettagli luogo:', error);
      setIsLoading(false);
      setError('Errore nel recupero dei dettagli. Prova di nuovo.');
    }
  };

  // Gestisce il click fuori dal dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const remainingSearches = MAX_SEARCHES_PER_SESSION - searchCount;
  const isLimitReached = searchCount >= MAX_SEARCHES_PER_SESSION;

  // Se Google Maps è disabilitato, mostra un input semplice
  if (isGoogleMapsDisabled) {
    return (
      <div className={`${className}`}>
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={onFocus}
            placeholder="Inserisci indirizzo manualmente (Google Maps disabilitato)"
            disabled={disabled}
            className="w-full px-4 py-3 pl-12 bg-zinc-800 border border-zinc-600 rounded-lg text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:border-zinc-500 focus:ring-zinc-500/20 transition-colors"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Campo di input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={onFocus}
          placeholder={placeholder}
          disabled={disabled || isLimitReached || !googleLoaded}
          className={`w-full px-4 py-3 pl-12 bg-zinc-800 border rounded-lg text-white placeholder-zinc-400 transition-colors focus:outline-none focus:ring-2 ${
            disabled || isLimitReached || !googleLoaded
              ? 'border-zinc-600 opacity-60 cursor-not-allowed'
              : error
              ? 'border-red-500 focus:ring-red-500/20'
              : 'border-zinc-600 focus:border-zinc-500 focus:ring-zinc-500/20'
          }`}
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
        
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Loader className="animate-spin w-5 h-5 text-zinc-400" />
          </div>
        )}
      </div>

      {/* Messaggi di stato */}
      {!googleLoaded && !isGoogleMapsDisabled && (
        <div className="flex items-center gap-1 mt-1 text-zinc-500 text-xs">
          <Loader className="animate-spin w-3 h-3" />
          <span>Caricamento Google Maps...</span>
        </div>
      )}

      {/* Contatore ricerche rimanenti */}
      {remainingSearches <= 3 && remainingSearches > 0 && (
        <div className="flex items-center gap-1 mt-1 text-amber-400 text-xs">
          <AlertCircle size={12} />
          <span>Ricerche rimanenti: {remainingSearches}</span>
        </div>
      )}

      {/* Messaggio limite raggiunto */}
      {isLimitReached && (
        <div className="flex items-center gap-1 mt-1 text-red-400 text-xs">
          <AlertCircle size={12} />
          <span>Limite ricerche raggiunto. Inserimento manuale disponibile.</span>
        </div>
      )}

      {/* Messaggio errore */}
      {error && (
        <div className="flex items-center gap-1 mt-1 text-red-400 text-xs">
          <AlertCircle size={12} />
          <span>{error}</span>
        </div>
      )}

      {/* Dropdown risultati */}
      {showDropdown && predictions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-zinc-800 border border-zinc-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {predictions.map((prediction) => (
            <button
              key={prediction.place_id}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handlePlaceSelect(prediction);
              }}
              className="w-full px-4 py-3 text-left text-white hover:bg-zinc-700 transition-colors border-b border-zinc-600 last:border-b-0 focus:outline-none focus:bg-zinc-700"
            >
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">
                    {prediction.structured_formatting?.main_text}
                  </div>
                  <div className="text-xs text-zinc-400 truncate">
                    {prediction.structured_formatting?.secondary_text}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Utility function per debounce
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}